import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { cloneDeep, isEqual } from 'lodash-es';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';

import { SaveChangesComponent } from '@features/save-changes/save-changes.component';

import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import {
  EventType,
  GqlService,
  listNotificationPreferencesQuery,
  updateNotificationPreferenceMutation,
} from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { MainQuery } from '@shared/store/main/main.query';
import { batchPromises } from '@shared/utils';

type AccountNotification = Record<string, { enabled: boolean; event_type?: EventType }>;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CheckboxComponent, FormsModule, SaveChangesComponent, AsyncPipe],
  selector: 'aux-account-notifications',
  templateUrl: './account-notifications.component.html',
})
export class AccountNotificationsComponent {
  public authQuery = inject(AuthQuery);

  private cdr = inject(ChangeDetectorRef);
  private gqlService = inject(GqlService);
  private launchDarklyService = inject(LaunchDarklyService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);

  edits = new Set<EventType>();
  notifications: Record<string, AccountNotification> = {
    changeOrder: {
      approved: { enabled: false },
      backToPendingReview: { enabled: false },
      declined: { enabled: false },
      deleted: { enabled: false },
      pendingApproval: { enabled: false },
      pendingReview: { enabled: false },
      request: { enabled: false },
      requestConfirmation: { enabled: false },
    },
    forecast: {
      forecastUpdated: { enabled: false },
    },
    invoices: {
      pendingApproval: { enabled: false },
      uploadInvoicesReminder: { enabled: false },
    },
    periodClose: {
      completeChecklistReminder: { enabled: false },
      edcDocumentsReadyForReview: { enabled: false },
      monthCloseAvailable: { enabled: false },
      monthClosed: { enabled: false },
      quarterClosed: { enabled: false },
      uploadVendorEstimatesReminder: { enabled: false },
    },
    support: {
      invoiceCategorizationReminder: { enabled: false },
      invoicesPendingReview: { enabled: false },
      journalEntryReportRequested: { enabled: false },
      purchaseOrderBulkUploadNotification: { enabled: false },
      sendEmailBudgetSnapshot: { enabled: false },
      sendEmailDocumentsUploaded: { enabled: false },
      sendEmailEDCDocumentsUploaded: { enabled: false },
      sendEmailSiteBudgetEffectiveDateUpdated: { enabled: false },
      sendEmailSiteContractUploaded: { enabled: false },
      sendEmailTrialProtocolUploaded: { enabled: false },
      sendEmailVendorDocumentUploaded: { enabled: false },
      vendorEstimateUploaded: { enabled: false },
    },
  };
  initialValues$ = new BehaviorSubject(this.notifications);
  originalData: listNotificationPreferencesQuery[] | null = null;
  saveCheck$ = new BehaviorSubject(false);
  showRequestJournalEntryPreference$: Observable<boolean>;

  async canDeactivate(): Promise<boolean> {
    if (this.saveCheck$.getValue()) {
      const result = this.overlayService.openUnsavedChangesConfirmation();
      const event = await firstValueFrom(result.afterClosed$);

      return !!event.data;
    }

    return true;
  }

  constructor() {
    this.gqlService
      .listNotificationPreferences$()
      .pipe(takeUntilDestroyed())
      .subscribe(({ data }) => this.initNotificationData(data));

    this.showRequestJournalEntryPreference$ = this.launchDarklyService.select$(
      (flags) => flags.request_journal_entry_report_button
    );
  }

  private initNotificationData(data: Array<listNotificationPreferencesQuery> | null): void {
    this.notificationMapper(data);
    this.originalData = data;
    this.initialValues$.next(cloneDeep(this.notifications));
  }

  isFormEqual() {
    return isEqual(this.notifications, this.initialValues$.getValue());
  }

  isGroupChecked(obj: AccountNotification) {
    return Object.values(obj).every(({ enabled }) => enabled);
  }

  isGroupIndeterminate(obj: AccountNotification) {
    return (
      Object.values(obj).some(({ enabled }) => enabled) &&
      Object.values(obj).some(({ enabled }) => !enabled)
    );
  }

  // eslint-disable-next-line max-lines-per-function
  notificationMapper(data: listNotificationPreferencesQuery[] | null) {
    // eslint-disable-next-line max-lines-per-function, complexity
    (data || []).forEach(({ enabled, notification }) => {
      const event_type = notification.event_type.replace('EVENT_TYPE_', '') as EventType;

      switch (notification.event_type_group) {
        case 'EVENT_TYPE_GROUP_BUDGET':
          if (event_type === 'BUDGET_SNAPSHOT_NOTIFICATION') {
            this.notifications.support.sendEmailBudgetSnapshot.event_type = EventType[event_type];
            this.notifications.support.sendEmailBudgetSnapshot.enabled = enabled;
          } else if (event_type === 'MONTH_AVAILABLE_TO_CLOSE') {
            this.notifications.periodClose.monthCloseAvailable.event_type = EventType[event_type];
            this.notifications.periodClose.monthCloseAvailable.enabled = enabled;
          } else if (event_type === 'TRIAL_MONTH_CLOSED') {
            this.notifications.periodClose.monthClosed.event_type = EventType[event_type];
            this.notifications.periodClose.monthClosed.enabled = enabled;
          } else if (event_type === 'VENDOR_ESTIMATE_SUPPORTING_DOCUMENT_UPLOADED') {
            this.notifications.support.vendorEstimateUploaded.event_type = EventType[event_type];
            this.notifications.support.vendorEstimateUploaded.enabled = enabled;
          } else if (event_type === 'QUARTER_CLOSED') {
            this.notifications.periodClose.quarterClosed.event_type = EventType[event_type];
            this.notifications.periodClose.quarterClosed.enabled = enabled;
          } else if (event_type === 'UPLOAD_VENDOR_ESTIMATE_REMINDER') {
            this.notifications.periodClose.uploadVendorEstimatesReminder.event_type =
              EventType[event_type];
            this.notifications.periodClose.uploadVendorEstimatesReminder.enabled = enabled;
          } else if (event_type === 'COMPLETE_CHECKLIST_REMINDER') {
            this.notifications.periodClose.completeChecklistReminder.event_type =
              EventType[event_type];
            this.notifications.periodClose.completeChecklistReminder.enabled = enabled;
          } else if (event_type === 'CATEGORIZE_INVOICES_REMINDER') {
            this.notifications.support.invoiceCategorizationReminder.event_type =
              EventType[event_type];
            this.notifications.support.invoiceCategorizationReminder.enabled = enabled;
          }

          break;
        case 'EVENT_TYPE_GROUP_CHANGE_ORDER':
          if (event_type === 'CHANGE_ORDER_APPROVED') {
            this.notifications.changeOrder.approved.event_type = EventType[event_type];
            this.notifications.changeOrder.approved.enabled = enabled;
          } else if (event_type === 'CHANGE_ORDER_DECLINED') {
            this.notifications.changeOrder.declined.event_type = EventType[event_type];
            this.notifications.changeOrder.declined.enabled = enabled;
          } else if (event_type === 'CHANGE_ORDER_DELETED') {
            this.notifications.changeOrder.deleted.event_type = EventType[event_type];
            this.notifications.changeOrder.deleted.enabled = enabled;
          } else if (event_type === 'CHANGE_ORDER_PENDING_APPROVAL') {
            this.notifications.changeOrder.pendingApproval.event_type = EventType[event_type];
            this.notifications.changeOrder.pendingApproval.enabled = enabled;
          } else if (event_type === 'CHANGE_ORDER_REQUEST_CONFIRMATION_NOTIFICATION') {
            this.notifications.changeOrder.requestConfirmation.event_type = EventType[event_type];
            this.notifications.changeOrder.requestConfirmation.enabled = enabled;
          } else if (event_type === 'CHANGE_ORDER_REQUEST_NOTIFICATION') {
            this.notifications.changeOrder.request.event_type = EventType[event_type];
            this.notifications.changeOrder.request.enabled = enabled;
          } else if (
            event_type === 'CHANGE_ORDER_PENDING_REVIEW' ||
            event_type === 'CHANGE_ORDER_BACK_TO_PENDING_REVIEW'
          ) {
            this.notifications.changeOrder.pendingReview.event_type =
              EventType.CHANGE_ORDER_PENDING_REVIEW;
            this.notifications.changeOrder.pendingReview.enabled = enabled;
            this.notifications.changeOrder.backToPendingReview.event_type =
              EventType.CHANGE_ORDER_BACK_TO_PENDING_REVIEW;
            this.notifications.changeOrder.backToPendingReview.enabled = enabled;
          }

          break;
        case 'EVENT_TYPE_GROUP_CLIENT_REQUEST':
          if (event_type === 'REQUEST_JOURNAL_ENTRY_REPORT_NOTIFICATION') {
            this.notifications.support.journalEntryReportRequested.event_type =
              EventType[event_type];
            this.notifications.support.journalEntryReportRequested.enabled = enabled;
          }

          break;
        case 'EVENT_TYPE_GROUP_DOCUMENT':
          if (event_type === 'DOCUMENT_UPLOADED_NOTIFICATION') {
            this.notifications.support.sendEmailDocumentsUploaded.event_type =
              EventType[event_type];
            this.notifications.support.sendEmailDocumentsUploaded.enabled = enabled;
          }

          if (event_type === 'EDC_DOCUMENT_UPLOADED_NOTIFICATION') {
            this.notifications.support.sendEmailEDCDocumentsUploaded.event_type =
              EventType[event_type];
            this.notifications.support.sendEmailEDCDocumentsUploaded.enabled = enabled;
          }

          if (event_type === EventType.EDC_DOCUMENT_READY_FOR_REVIEW_NOTIFICATION) {
            this.notifications.periodClose.edcDocumentsReadyForReview.event_type =
              EventType[event_type];
            this.notifications.periodClose.edcDocumentsReadyForReview.enabled = enabled;
          }

          if (event_type === EventType.PURCHASE_ORDER_BULK_UPLOAD_NOTIFICATION) {
            this.notifications.support.purchaseOrderBulkUploadNotification.event_type =
              EventType[event_type];
            this.notifications.support.purchaseOrderBulkUploadNotification.enabled = enabled;
          }

          if (event_type === 'SITE_CONTRACT_UPLOADED') {
            this.notifications.support.sendEmailSiteContractUploaded.event_type =
              EventType[event_type];
            this.notifications.support.sendEmailSiteContractUploaded.enabled = enabled;
          }

          if (event_type === 'SITE_BUDGET_EFFECTIVE_DATE_UPDATED') {
            this.notifications.support.sendEmailSiteBudgetEffectiveDateUpdated.event_type =
              EventType[event_type];
            this.notifications.support.sendEmailSiteBudgetEffectiveDateUpdated.enabled = enabled;
          }

          if (event_type === 'TRIAL_PROTOCOL_UPLOADED') {
            this.notifications.support.sendEmailTrialProtocolUploaded.event_type =
              EventType[event_type];
            this.notifications.support.sendEmailTrialProtocolUploaded.enabled = enabled;
          }

          break;
        case 'EVENT_TYPE_GROUP_INVOICE':
          if (event_type === 'INVOICE_PENDING_APPROVAL') {
            this.notifications.invoices.pendingApproval.event_type = EventType[event_type];
            this.notifications.invoices.pendingApproval.enabled = enabled;
          } else if (event_type === 'UPLOAD_INVOICE_REMINDER') {
            this.notifications.invoices.uploadInvoicesReminder.event_type = EventType[event_type];
            this.notifications.invoices.uploadInvoicesReminder.enabled = enabled;
          } else if (event_type === 'INVOICE_PENDING_REVIEW') {
            this.notifications.support.invoicesPendingReview.event_type = EventType[event_type];
            this.notifications.support.invoicesPendingReview.enabled = enabled;
          }

          break;
        case 'EVENT_TYPE_GROUP_VENDOR':
          if (event_type === 'VENDOR_DOCUMENT_UPLOADED_NOTIFICATION') {
            this.notifications.support.sendEmailVendorDocumentUploaded.event_type =
              EventType[event_type];
            this.notifications.support.sendEmailVendorDocumentUploaded.enabled = enabled;
          }

          break;
        default:
          break;
      }
    });
    this.cdr.detectChanges();
  }

  onChange(event_types: (EventType | undefined)[]) {
    event_types?.forEach((event_type) => {
      if (!event_type) {
        return;
      }

      this.edits.add(event_type);

      setTimeout(() => {
        this.saveCheck$.next(!this.isFormEqual());
      }, 0);
    });
  }

  onGroupChange(group: string) {
    const notificationGroup = this.notifications[group];
    const newValue = !this.isGroupChecked(notificationGroup);

    Object.values(notificationGroup).forEach((notification) => {
      if (notification.event_type) {
        Object.assign(notification, { enabled: newValue });
        this.onChange([notification.event_type]);
      }
    });
    this.cdr.detectChanges();
  }

  reset = () => {
    this.notificationMapper(this.originalData);
    this.initialValues$.next(cloneDeep(this.notifications));
    this.edits.clear();
    this.saveCheck$.next(false);
  };

  saveChanges = async () => {
    const promises: Promise<null | {
      data: null | updateNotificationPreferenceMutation;
      errors: string[];
      success: boolean;
    }>[] = [];
    const flat = Object.values(this.notifications).reduce(
      (acc, notificationGroup) => acc.concat(Object.values(notificationGroup)),
      [] as {
        enabled: boolean;
        event_type?: EventType;
      }[]
    );

    for (const event_type of this.edits) {
      const checkbox = flat.find((el) => event_type === el.event_type);

      if (checkbox) {
        promises.push(
          firstValueFrom(
            this.gqlService.updateNotificationPreference$({ enabled: checkbox.enabled, event_type })
          )
        );
      }
    }

    const responses = await batchPromises(promises, (p) => p);

    if (responses.every((r) => !(r instanceof Error) && r?.success)) {
      responses.forEach((response) => {
        const r = response as Exclude<typeof response, Error>;

        this.originalData?.forEach(({ notification }, index) => {
          if (notification.id === r?.data?.notification.id) {
            const obj = this.originalData && this.originalData[index];

            if (obj) {
              obj.enabled = r?.data?.enabled;
            }
          }
        });
      });
      this.saveCheck$.next(false);
      this.overlayService.success('Changes successfully saved!');
      this.edits.clear();
      this.initialValues$.next(cloneDeep(this.notifications));
    } else {
      this.overlayService.error('Something went wrong');
    }
  };
}
